import React from "react";
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  TablePagination,
} from "@mui/material";
import {
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import { useTranslate } from "src/locales";

export interface HistoryGridRow {
  id: number | string;
  requestDate: string;
  operationName: string;
  quantity: string;
  note: string;
  isParent: boolean;
  children?: HistoryGridRow[];
  originalData?: any;
}

export interface HistoryGridProps {
  rows: HistoryGridRow[];
  selectedHistoryServices: (number | string)[];
  expandedHistoryRows: string[];
  historyParentPage: number;
  historyParentPageSize: number;
  totalParentCount: number;
  loading?: boolean;
  onHistoryServiceToggle: (serviceId: number | string, originalData?: any, associatedHistoryId?: number | string) => void;
  onHistoryParentToggle: (parentId: number | string) => void;
  onParentToggle: (parentId: string) => void;
  onHistoryParentPageChange: (event: unknown, newPage: number) => void;
  onHistoryParentRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddToFavorites?: (rowId: number | string) => Promise<void>;
  showAddToFavorites?: boolean;
}

const HistoryGrid: React.FC<HistoryGridProps> = ({
  rows,
  selectedHistoryServices,
  expandedHistoryRows,
  historyParentPage,
  historyParentPageSize,
  totalParentCount,
  loading = false,
  onHistoryServiceToggle,
  onHistoryParentToggle,
  onParentToggle,
  onHistoryParentPageChange,
  onHistoryParentRowsPerPageChange,
  onAddToFavorites,
  showAddToFavorites = false,
}) => {
  const { t } = useTranslate();

  const historyColumns: GridColDef[] = [
    {
      field: "select",
      headerName: "",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minWidth: "150px",
            height: "40px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Checkbox
            checked={false}
            onChange={() => {}}
            sx={{
              color: "#333",
              "&.Mui-checked": {
                color: "#333",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              height: "56px",
              width: "1px",
              backgroundColor: "#666",
              zIndex: 1,
            }}
          />
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pl: params.row.isParent ? 0 : 3,
            position: "relative",
            width: "100%",
            minWidth: "150px",
          }}
        >
          {!params.row.isParent && (
            <SubdirectoryArrowRightIcon sx={{ fontSize: 16, color: "#666" }} />
          )}
          <Checkbox
            checked={
              params.row.isParent
                ? params.row.children?.every((child: any) =>
                    selectedHistoryServices.includes(child.id)
                  ) || false
                : selectedHistoryServices.includes(params.row.id)
            }
            onChange={() => {
              if (params.row.isParent) {
                onHistoryParentToggle(params.row.id);
              } else {
                onHistoryServiceToggle(params.row.id, params.row.originalData, params.row.id);
              }
            }}
            sx={{
              color: "#333",
              "&.Mui-checked": {
                color: "#333",
              },
            }}
          />
          {!params.row.isParent && showAddToFavorites && onAddToFavorites && (
            <ByzMuiOpenProcessMenu
              menuItems={[
                {
                  label: t("patient-request-service.favorites.add"),
                  iconColor: "success",
                  onClick: async () => {
                    await onAddToFavorites(params.row.id);
                  },
                },
              ]}
              onClose={() => {}}
              sx={{ marginRight: "8px" }}
            />
          )}
          {params.row.isParent && (
            <IconButton
              size="small"
              onClick={() => onParentToggle(params.row.id.toString())}
            >
              {expandedHistoryRows.includes(params.row.id.toString()) ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
            </IconButton>
          )}
          {/* Dikey border - işlemler ve data alanını ayırır */}
          <Box
            sx={{
              position: "absolute",
              right: params.row.isParent ? "calc(100% - 150px)" : 0,
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: "#666",
            }}
          />
        </Box>
      ),
    },
    {
      field: "requestDate",
      headerName: t("patient-request-service.grid.columns.request-date"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: params.row.isParent ? "bold" : "normal",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "operationName",
      headerName: t("patient-request-service.grid.columns.operation-name"),
      flex: 1,
      minWidth: 200,
    },
    {
      field: "quantity",
      headerName: t("patient-request-service.grid.columns.quantity"),
      width: 100,
    },
    {
      field: "note",
      headerName: t("patient-request-service.grid.columns.note"),
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxHeight: "60vh",
          overflow: "auto",
          "& .MuiDataGrid-root": { height: "auto !important" },
        }}
      >
        <ByzMuiDataGrid
          key={`history-${expandedHistoryRows.length}`}
          rows={rows}
          columns={historyColumns}
          getRowId={(row) => row.id}
          checkboxSelection={false}
          disableColumnMenu={false}
          autoHeight
          loading={loading}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 200,
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("patient-request-service.grid.no-history")}
                </Typography>
                <Typography variant="body2">
                  {t("patient-request-service.grid.no-history-desc")}
                </Typography>
              </Box>
            ),
          }}
          sx={{
            width: "100%",
            height: "auto",
            "& .MuiDataGrid-row": {
              minHeight: "40px !important",
              maxHeight: "40px !important",
              borderBottom: "none !important",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none !important",
              height: "40px !important",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid #666 !important",
              backgroundColor: "white !important",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "white !important",
              borderBottom: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#333 !important",
              fontWeight: "600 !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              height: "auto !important",
              "& .MuiDataGrid-row": {
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "1px",
                  backgroundColor: "#e0e0e0",
                },
              },
            },
            "& .MuiDataGrid-footerContainer": {
              display: "none !important",
            },
          }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <TablePagination
          component="div"
          count={totalParentCount}
          page={historyParentPage}
          onPageChange={onHistoryParentPageChange}
          rowsPerPage={historyParentPageSize}
          onRowsPerPageChange={onHistoryParentRowsPerPageChange}
          rowsPerPageOptions={[3, 5, 10]}
          labelRowsPerPage={t(
            "patient-request-service.grid.pagination.parent-per-page"
          )}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count} parent`
          }
        />
      </Box>
    </>
  );
};

export default HistoryGrid;
